<template>
	<form @submit.prevent="save" @reset.prevent="cancel" class="expense-editor">
		<!-- Who paid -->
		<div class="form-group">
			<label v-i18n="'expense_whoPaid'" class="form-control-label"></label>
			<select v-model.number="payorId" class="form-control"
			        ref="payorSelect" :disabled="disabled">
				<option v-for="booker of sortedBookers" :value="booker.id" :key="booker.id">{{booker.name}}
				</option>
			</select>
		</div>

		<!-- Who pays -->
		<div class="form-group">
			<div class="input-split">
				<!-- Split label -->
				<div @click="openSplitModal">
					<label v-i18n="'expense_whoPays'" class="form-control-label"></label>
					<div class="form-control" style="cursor:pointer" :readonly="disabled && formPristine">
						{{$formatMessage('split_type_label', {type: splitType, totalUnits: splitTotalUnits, bookerName:
						splitBookerName})}}
					</div>
				</div>

				<!-- Split modal -->
				<modal modal-class="modal-input-split" v-on:apply="applySplit" :apply-disabled="splitInvalid"
				       ref="splitModal" title="expense_input_split_modal_title">
					<div class="form-check">
						<label class="form-check-label">
							<input type="checkbox" v-model="hasDefaultSplit" class="form-check-input"
							       :disabled="disabled"/>
							{{$formatMessage('expense_split_default')}}
						</label>
					</div>
					<input-split :split="editedSplit" :sheet="sheet"
					             :disabled="hasDefaultSplit || disabled"></input-split>
				</modal>
			</div>
		</div>
		<div class="clearfix visible-sm-up d-md-none"></div>

		<!-- What -->
		<div class="form-group">
			<label v-i18n="'expense_what'" class="form-control-label"></label>
			<textarea v-model="description" class="form-control"
			          :disabled="disabled"></textarea>
		</div>

		<!-- How much -->
		<div class="form-group">
			<label v-i18n="'expense_howMuch'" class="form-control-label"></label>
			<input-amount v-model.number="amount" :currency="sheet.parent.currency"
			              ref="amountInput" :disabled="disabled"></input-amount>
		</div>

		<!-- Buttons -->
		<div class="text-right">
			<button class="btn btn-secondary" v-i18n="'cancel'" type="reset"
			        :disabled="cannotCancel"></button>
			<button class="btn btn-primary" v-i18n="'save'" type="submit" :disabled="cannotSubmit"
			        v-if="!disabled"></button>
		</div>
	</form>
</template>

<script>
	import store, {ChildrenObserver} from "../core/store";
	import {Row, HasSplit} from '../core/entities';
	import InputSplit from './widgets/input-split.vue';
	import InputAmount from './widgets/input-amout.vue';
	import Modal from './widgets/modal.vue';
	import toast from "./toast";
	import PimplWrapper from "../utils/pimpl";


	class Pimpl extends ChildrenObserver {
		constructor(component) {
			super(component.sheet.key);
			this.component = component;
		}

		_onChanged(event, params) {
			if (params.kind === "Sheeter") {
				this.component.reset(true);
			}
		}

		resetEditedSplit() {
			const component = this.component;
			component.editedSplit = component.split ?
				HasSplit.copySplit(component.split) :
				component.sheet.createSplit();
		}

		getEditedExpense(expense) {
			const component = this.component;
			expense.payor_id = component.payorId;
			expense.amount = component.amount;
			expense.description = component.description;
			expense.assignSplit(component.split);
		}

		getSplit() {
			const component = this.component;
			return component.hasDefaultSplit ? null : HasSplit.copySplit(component.editedSplit);
		}
	}

	Pimpl.prototype.onAdded = Pimpl.prototype._onChanged;
	Pimpl.prototype.onRemoved = Pimpl.prototype._onChanged;

	export default {
		props: {
			'sheet': Object,
			'rowId': Number,
			'disabled': Boolean
		},

		components: {
			InputSplit,
			InputAmount,
			Modal
		},

		data: function () {
			return {
				p: new PimplWrapper(new Pimpl(this)),
				split: null,
				hasDefaultSplit: true,
				editedSplit: this.sheet.createSplit(),
				description: "",
				amount: 0,
				payorId: null,
				sending: false,
				disableCancelWhenPristine: true
			};
		},

		computed: {
			sortedBookers() {
				return this.sheet.bookers().sort((s1, s2) => s1.name.localeCompare(s2.name));
			},
			splitType() {
				return this.split ? this.split.type : 'default';
			},
			splitTotalUnits() {
				return this.split ? this.split.totalUnits : -1;
			},
			splitBookerName() {
				return this.split ? this.sheet.getBooker(this.split.bookerId).name : '';
			},
			splitInvalid() {
				const split = this.p.impl.getSplit();
				return !(split === null || split.valid);
			},
			cannotCancel() {
				return this.disableCancelWhenPristine && this.formPristine || this.sending;
			},
			cannotSubmit() {
				let canSubmit = this.payorId && this.amount > 0 && !this.sending;
				if (canSubmit && this.rowId) {
					const stored = store.get(this.sheet.key, 'Row', this.rowId);
					const edited = stored.clone();
					this.p.impl.getEditedExpense(edited);
					canSubmit = !stored.equals(edited);
				}
				return !canSubmit;
			},
			formPristine() {
				return !(this.payorId || this.split || this.description || this.amount);
			}
		},

		methods: {
			save() {
				if (this.cannotSubmit) // may happen when hitting enter on IE
					return;
				this.sending = true;
				const expense = this.rowId ?
					store.get(this.sheet.key, 'Row', this.rowId).clone() :
					new Row(this.sheet, null);
				this.p.impl.getEditedExpense(expense);
				let resetSending = () => this.sending = false;
				let saved = (saved) => {
					if (saved || saved === undefined) {
						this.$emit('saved');
						toast.tr('expense_saved');
					}
					this.focus();
					resetSending();
				};
				if (this.rowId) {
					expense.update().then((event) => {
						saved(Boolean(event))
					}, resetSending);
				} else {
					this.sheet.addChildren([expense]).then(saved, resetSending);
				}
			},
			cancel() {
				this.$emit('cancelled');
				if (!this.rowId) {
					this.reset();
				}
			},
			reset(onlySplit = false) {
				if (this.rowId) {
					let expense = store.get(this.sheet.key, 'Row', this.rowId).clone();
					if (!onlySplit) {
						this.description = expense.description;
						this.amount = expense.amount;
						this.payorId = expense.payor_id;
					}
					this.split = expense.cloneSplit();
				} else {
					if (!onlySplit) {
						this.description = "";
						this.amount = 0;
						this.payorId = null;
					}
					this.split = null;
				}
				this.p.impl.resetEditedSplit();
			},
			toggleSplit($event, part) {
				part.value = Number($event.target.checked)
			},
			openSplitModal() {
				this.hasDefaultSplit = !this.split;
				this.p.impl.resetEditedSplit();
				this.$refs.splitModal.show();
			},
			applySplit() {
				let split = this.p.impl.getSplit();
				if (split === null || split.valid) {
					this.split = split;
					this.$refs.splitModal.hide();
				}
			},
			focus() {
				if (this.rowId) {
					// Do not set focus automatically: annoying on mobile
					// if I don't want to edit this field (shows the keyboard)
					//this.$refs.amountInput.focusInput();
				} else {
					$(this.$refs.payorSelect).select().focus();
				}
			}
		},

		watch: {
			'rowId': function () {
				this.reset();
				if (!this.disabled) {
					this.focus();
				}
			}
		},

		mounted() {
			this.p = new PimplWrapper(new Pimpl(this));
			this.p.impl.register();
			setTimeout(() => {
				this.focus()
			}, 0);
		},

		beforeDestroy() {
			this.$refs.splitModal.hide();
		},

		destroyed() {
			this.p.impl.unregister();
		}
	}
</script>

<style>
	.modal-input-split .modal-content {
		max-width: 20rem;
		margin: 0 auto;
	}
</style>


// WEBPACK FOOTER //
// src/ui/expense-editor.vue